import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import AccessGate from '../components/AccessGate';
import { Search, Users, ChevronRight, Shield } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Link } from 'react-router-dom';

export default function MembersDirectory() {
  const { getDirectory, mediaUrl, classLabel } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    getDirectory().then(setMembers).catch(() => setMembers([])).finally(() => setLoading(false));
  }, [getDirectory]);

  const term = q.trim().toLowerCase();
  const filtered = members.filter((m) =>
    !term ||
    (m.name || '').toLowerCase().includes(term) ||
    (m.rank || '').toLowerCase().includes(term) ||
    (m.unit || '').toLowerCase().includes(term)
  );

  return (
    <AccessGate pageKey="members-directory">
      <div className="max-w-4xl mx-auto px-4 py-10 fade-in">
        <h1 className="font-title text-3xl md:text-4xl mb-2" style={{ color: 'var(--maroon)' }}>Members Directory</h1>
        <div className="h-1 w-20 mb-4" style={{ background: 'var(--gold)' }} />
        <p className="text-[15px] mb-5">A roster of the Association's financial Sappers. Search by name, rank or unit.</p>

        <div className="relative mb-5 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--olive)' }} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members…" className="pl-9" />
        </div>

        {loading ? (
          <p className="text-sm" style={{ color: 'var(--olive)' }}>Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-lg p-6 shadow-sm border text-center" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <Users size={32} className="mx-auto mb-2" style={{ color: 'var(--olive)' }} />
            <p>{members.length === 0 ? 'No financial members are listed yet.' : 'No members match your search.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'var(--navy)' }}>
                  {['Name', 'Rank', 'Unit', 'Years Served', 'Standing', ''].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((m, i) => (
                  <tr key={m.user_id || i} className="transition-colors hover:bg-[var(--sage-light)]" style={{ background: i % 2 ? 'rgba(0,0,0,0.03)' : 'white' }}>
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-2.5">
                        {mediaUrl(m.photo_url) ? (
                          <img src={mediaUrl(m.photo_url)} alt={m.name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" style={{ border: '1px solid var(--gold)' }} />
                        ) : (
                          <span className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0" style={{ background: 'var(--navy)', border: '1px solid var(--gold)' }}>
                            <Shield size={15} color="var(--gold)" />
                          </span>
                        )}
                        <span className="flex flex-col min-w-0">
                          <Link to={`/members/directory/${m.user_id}`} className="hover:underline truncate" style={{ color: 'var(--navy)', fontWeight: 600 }}>{m.name}</Link>
                          {m.email && <span className="text-xs truncate" style={{ color: 'var(--olive)' }}>{m.email}</span>}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-2.5">{m.rank || <span style={{ color: 'var(--olive)' }}>—</span>}</td>
                    <td className="px-4 py-2.5">{m.unit || <span style={{ color: 'var(--olive)' }}>—</span>}</td>
                    <td className="px-4 py-2.5">{m.years_served || <span style={{ color: 'var(--olive)' }}>—</span>}</td>
                    <td className="px-4 py-2.5">
                      <span className="px-2 py-0.5 rounded text-xs font-title" style={{ background: 'var(--sage)', color: 'var(--ink)' }}>
                        {classLabel(m.member_class)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <Link to={`/members/directory/${m.user_id}`} className="inline-flex items-center gap-0.5 text-xs" style={{ color: 'var(--maroon)' }}>
                        View <ChevronRight size={13} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs mt-3" style={{ color: 'var(--olive)' }}>Showing {filtered.length} of {members.length} members.</p>
      </div>
    </AccessGate>
  );
}
